import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import posthog from '../../lib/posthog';
import { useAuth } from '../../hooks/useAuth';
import { useIsDarkTheme } from '../../hooks/useTheme';
import { RevealStyles, reveal } from '../../components/design-system';
import { JourneySidebar } from '../../components/traveler/JourneySidebar';
import fallingCoinsThemeDarkUrl from '../../assets/animations/fallingcoins-theme-dark.webp?url';
import fallingCoinsThemeLightUrl from '../../assets/animations/fallingcoins-theme-light.webp?url';
import checkmarkThemeDarkUrl from '../../assets/illustrations/checkmark-theme-dark.svg?url';
import checkmarkThemeLightUrl from '../../assets/illustrations/checkmark-theme-light.svg?url';
import mapLookingThemeDarkUrl from '../../assets/illustrations/sekretly-map-looking-theme-dark.svg?url';
import mapLookingThemeLightUrl from '../../assets/illustrations/sekretly-map-looking-theme-light.svg?url';

// The post-payment payoff, timed in beats:
//   1. "Confirming payment…" fades out (CONFIRMING_FADE_OUT_S)
//   2. each CONFIRMED_BEATS entry reveals in (illustration + headline,
//      staggered — see below), holds for CONFIRMED_HOLD_MS, then reverses
//      that same reveal — not a plain fade — before the next beat (or the
//      actual navigation, after the last one) fires. Every beat plays the
//      identical motion; only the illustration/headline differ.
const CONFIRMED_BEATS: { illustration: { dark: string; light: string }; illustrationHeight: number; headline: string }[] = [
  // The checkmark is a single bold stroke, not fine line art like the map
  // illustration — at the same 151px height (matching the coin video) it
  // reads much heavier/bigger since it fills more of its own bounding box.
  { illustration: { dark: checkmarkThemeDarkUrl, light: checkmarkThemeLightUrl }, illustrationHeight: 100, headline: 'Payment confirmed.' },
  { illustration: { dark: mapLookingThemeDarkUrl, light: mapLookingThemeLightUrl }, illustrationHeight: 151, headline: 'We got you now.' },
];
const CONFIRMING_FADE_OUT_S = 0.6;
const CONFIRMED_ILLUSTRATION_DELAY_S = 0.1;
const CONFIRMED_ILLUSTRATION_DURATION_S = 0.8;
const CONFIRMED_HEADLINE_DELAY_S = 0.35;
const CONFIRMED_HEADLINE_DURATION_S = 0.65;
const CONFIRMED_REVEAL_IN_MS = Math.max(
  CONFIRMED_ILLUSTRATION_DELAY_S + CONFIRMED_ILLUSTRATION_DURATION_S,
  CONFIRMED_HEADLINE_DELAY_S + CONFIRMED_HEADLINE_DURATION_S,
) * 1000;
const CONFIRMED_REVEAL_OUT_MS = Math.max(CONFIRMED_ILLUSTRATION_DURATION_S, CONFIRMED_HEADLINE_DURATION_S) * 1000;
// How long "Payment confirmed" holds before reversing out.
// Halved from the original single-screen value now that there are two
// beats back to back — same total hold budget across the pair, but neither
// one lingers as long individually.
const CONFIRMED_HOLD_MS = 1500;
// "Confirming payment…" fading out the instant `verify()` resolves can be
// near-instant on the server-check path — this floors how long it stays on
// screen regardless of how fast verification actually was.
const MIN_CONFIRMING_DISPLAY_MS = 1800;

export function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { activeRole, switchRole } = useAuth();
  const isDarkTheme = useIsDarkTheme();
  const [status, setStatus] = useState<'verifying' | 'paid' | 'failed'>('verifying');
  // Separate from `status` — this drives the visual sequence once paid,
  // independent of whatever verification path (server check vs. polling)
  // actually detected it.
  const [uiStage, setUiStage] = useState<'confirming' | 'confirming-exit' | 'beat-enter' | 'beat-exit'>('confirming');
  const [beatIndex, setBeatIndex] = useState(0);
  // Read via ref inside the verify effect below — switchRole's identity
  // changes with every profile update, and re-running payment verification
  // because of that would be wrong; only the values at the moment of success
  // matter.
  const roleRef = useRef({ activeRole, switchRole });
  roleRef.current = { activeRole, switchRole };
  const mountedAtRef = useRef(Date.now());

  useEffect(() => {
    // Payment now happens in-page via a Checkout Session (ui_mode: 'elements')
    // — Payment.tsx routes here itself on success with `session_id` set.
    // Stripe would also append this same param if a payment method ever
    // required an actual redirect back.
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      // No session — fall back to checking for any paid payment
      navigate('/matches', { replace: true });
      return;
    }

    let cancelled = false;

    // Once paid, the traveler's home is their trip chat (Alma history + waiting
    // on the host). Fall back to the trips list if we couldn't resolve a match.
    // Runs the full fade-out / reveal-in / hold / reveal-out beat sequence
    // (see the constants above) before actually navigating — see this
    // component's doc comment for the full choreography.
    function goToTrip(matchId: string | null) {
      // A multi-role account (e.g. an admin who also travels) can complete a
      // payment while `active_role` is still whatever they last switched to —
      // "admin" was showing in the trip sidebar identity right after paying,
      // because nothing here ever updated it. Completing a payment is an
      // unambiguous traveler action, so make it one.
      if (roleRef.current.activeRole !== 'traveler') void roleRef.current.switchRole('traveler');
      const target = matchId ? `/trip/${matchId}/chat` : '/home';
      setStatus('paid');

      function playBeat(index: number) {
        setBeatIndex(index);
        setUiStage('beat-enter');
        setTimeout(() => {
          setUiStage('beat-exit');
          setTimeout(() => {
            if (index + 1 < CONFIRMED_BEATS.length) {
              playBeat(index + 1);
            } else {
              navigate(target, { replace: true });
            }
          }, CONFIRMED_REVEAL_OUT_MS);
        }, CONFIRMED_REVEAL_IN_MS + CONFIRMED_HOLD_MS);
      }

      const elapsed = Date.now() - mountedAtRef.current;
      const untilMinDisplay = Math.max(0, MIN_CONFIRMING_DISPLAY_MS - elapsed);
      setTimeout(() => {
        setUiStage('confirming-exit');
        setTimeout(() => playBeat(0), CONFIRMING_FADE_OUT_S * 1000);
      }, untilMinDisplay);
    }

    async function verify() {
      // Try verifying the payment server-side (doesn't depend on webhook timing)
      try {
        const { data } = await supabase.functions.invoke('stripe-verify-payment', {
          body: { session_id: sessionId },
        });
        if (cancelled) return;
        if (data?.paid) {
          posthog.capture('payment_completed');
          goToTrip((data.match_id as string | null) ?? null);
          return;
        }
      } catch {
        /* fall through to polling */
      }

      // Fallback: poll the payments table a few times for a paid status
      for (let i = 0; i < 5 && !cancelled; i++) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: paid } = await supabase
            .from('payments')
            .select('match_id')
            .eq('traveler_id', user.id)
            .eq('status', 'paid')
            .order('updated_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          if (paid) {
            if (cancelled) return;
            posthog.capture('payment_completed');
            goToTrip((paid.match_id as string | null) ?? null);
            return;
          }
        }
        await new Promise((r) => setTimeout(r, 1500));
      }

      if (!cancelled) setStatus('failed');
    }

    void verify();
    return () => { cancelled = true; };
  }, [navigate, searchParams]);

  if (status === 'failed') {
    return (
      <div className="min-h-dvh bg-app-bg flex flex-col items-center justify-center px-6 text-center gap-6">
        <div className="w-16 h-16 rounded-full bg-crimson/15 flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-crimson">
            <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 00-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="flex flex-col gap-1">
          <h1 className="text-xl font-semibold text-app-fg">Still confirming your payment</h1>
          <p className="text-sm text-app-muted leading-relaxed max-w-xs">
            This can take a moment. If you completed payment, tap below to check again.
          </p>
        </div>
        <button
          onClick={() => navigate('/home', { replace: true })}
          className="text-sm font-medium text-gold underline underline-offset-2 sk-hit"
        >
          Go to my trip
        </button>
      </div>
    );
  }

  const showConfirming = uiStage === 'confirming' || uiStage === 'confirming-exit';
  const beatEntering = uiStage === 'beat-enter';
  const beat = CONFIRMED_BEATS[beatIndex];

  return (
    <div className="h-dvh flex bg-app-bg overflow-hidden">
      <RevealStyles />
      <JourneySidebar activeIndex={3} />
      <div className="flex-1 min-w-0 overflow-y-auto flex flex-col items-center justify-center px-6 py-8 text-center gap-9">
        {showConfirming ? (
          <>
            {/* Animated WebP, not a <video>: iOS won't autoplay a video in Low
                Power Mode and draws a play button over it; an image always plays. */}
            <img
              key={isDarkTheme ? 'dark' : 'light'}
              alt=""
              aria-hidden
              src={isDarkTheme ? fallingCoinsThemeDarkUrl : fallingCoinsThemeLightUrl}
              style={{
                width: 200, height: 'auto',
                ...(uiStage === 'confirming-exit' ? reveal('fade', 0, CONFIRMING_FADE_OUT_S, 'reverse') : null),
              }}
            />
            <h1
              className="font-serif text-page-title text-app-fg text-pretty"
              style={uiStage === 'confirming-exit' ? reveal('fade', 0, CONFIRMING_FADE_OUT_S, 'reverse') : undefined}
            >
              Confirming payment…
            </h1>
          </>
        ) : (
          <div key={beatIndex} className="flex flex-col items-center gap-9">
            <img
              src={isDarkTheme ? beat.illustration.dark : beat.illustration.light}
              alt=""
              style={{
                height: beat.illustrationHeight, width: 'auto',
                ...(beatEntering
                  ? reveal('focus', CONFIRMED_ILLUSTRATION_DELAY_S, CONFIRMED_ILLUSTRATION_DURATION_S)
                  : reveal('focus', 0, CONFIRMED_ILLUSTRATION_DURATION_S, 'reverse')),
              }}
            />
            <h1
              className="font-serif text-page-title text-app-fg text-pretty"
              style={
                beatEntering
                  ? reveal('rise', CONFIRMED_HEADLINE_DELAY_S, CONFIRMED_HEADLINE_DURATION_S)
                  : reveal('rise', 0, CONFIRMED_HEADLINE_DURATION_S, 'reverse')
              }
            >
              {beat.headline}
            </h1>
          </div>
        )}
      </div>
    </div>
  );
}

export default PaymentSuccess;
